import { useState, useEffect, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from 'recharts'
import {
  TicketIcon, CurrencyDollarIcon, CalendarDaysIcon, CheckBadgeIcon,
  ArrowTrendingUpIcon, ArrowTrendingDownIcon, MinusIcon,
  ArrowRightIcon, ArrowPathIcon,
} from '@heroicons/react/24/outline'
import { dashboardApi } from '@/api/dashboard'
import type { DashboardData, RecentEvent, RecentSale } from '@/types/dashboard'
import { useAuthStore } from '@/store/authStore'
import { Badge } from '@/components/ui/Badge'
import type { EventStatus } from '@/types/event'

// ─── Formatters ──────────────────────────────────────────────────────────────

function formatFCFA(amount: number): string {
  return `${amount.toLocaleString('fr-FR')} FCFA`
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: 'numeric', month: 'short', year: 'numeric',
  })
}

function formatChartDate(dateStr: string): string {
  const d = new Date(dateStr)
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
}

function timeAgo(isoStr: string | null): string {
  if (!isoStr) return '—'
  const diff = Date.now() - new Date(isoStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return "À l'instant"
  if (mins < 60) return `Il y a ${mins} min`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `Il y a ${hours}h`
  return `Il y a ${Math.floor(hours / 24)}j`
}

function initials(name: string | null, phone: string): string {
  if (name) {
    const parts = name.trim().split(' ')
    return parts.length >= 2
      ? (parts[0][0] + parts[1][0]).toUpperCase()
      : parts[0].slice(0, 2).toUpperCase()
  }
  return phone.slice(-2)
}

function avatarColor(str: string): string {
  const colors = [
    'bg-primary-100 text-primary-700',
    'bg-success-50 text-success-700',
    'bg-warning-50 text-warning-600',
    'bg-purple-100 text-purple-700',
    'bg-blue-100 text-blue-700',
  ]
  let hash = 0
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash)
  return colors[Math.abs(hash) % colors.length]
}

// ─── Trend ───────────────────────────────────────────────────────────────────

interface TrendProps {
  current: number
  previous: number
  suffix?: string
  invert?: boolean
}

function Trend({ current, previous, invert = false }: TrendProps) {
  if (previous === 0) {
    return <span className="text-xs text-gray-400 font-mono">—</span>
  }
  const pct = Math.round(((current - previous) / previous) * 100)
  const isUp = pct > 0
  const isGood = invert ? !isUp : isUp
  if (pct === 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-xs text-gray-400 font-mono">
        <MinusIcon className="w-3 h-3" />0%
      </span>
    )
  }
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-xs font-mono font-medium ${
        isGood ? 'text-success-600' : 'text-danger-600'
      }`}
    >
      {isUp
        ? <ArrowTrendingUpIcon className="w-3 h-3" />
        : <ArrowTrendingDownIcon className="w-3 h-3" />}
      {Math.abs(pct)}%
    </span>
  )
}

// ─── Custom Tooltip ───────────────────────────────────────────────────────────

function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-gray-900 text-white rounded-lg px-3 py-2 shadow-xl text-sm">
      <p className="text-gray-400 text-xs mb-1">{formatChartDate(label)}</p>
      <p className="font-mono font-medium">{payload[0]?.value} tickets</p>
      <p className="font-mono text-warning-500 text-xs">{formatFCFA(payload[1]?.value ?? 0)}</p>
    </div>
  )
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function SkeletonCard() {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse">
      <div className="flex items-start justify-between mb-4">
        <div className="w-9 h-9 bg-gray-100 rounded-lg" />
        <div className="w-10 h-4 bg-gray-100 rounded" />
      </div>
      <div className="w-24 h-7 bg-gray-100 rounded mb-2" />
      <div className="w-16 h-3 bg-gray-100 rounded" />
    </div>
  )
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────

interface KpiCardProps {
  icon: React.ReactNode
  label: string
  value: string
  trend?: React.ReactNode
  accent?: string
  delay?: number
}

function KpiCard({ icon, label, value, trend, accent = 'text-primary-600 bg-primary-50', delay = 0 }: KpiCardProps) {
  return (
    <div
      className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow duration-200"
      style={{ animationDelay: `${delay}ms`, animation: 'fadeSlideUp 0.4s ease both' }}
    >
      <div className="flex items-start justify-between mb-4">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${accent}`}>
          {icon}
        </div>
        {trend}
      </div>
      <p className="font-mono text-2xl font-bold text-gray-900 leading-none mb-1">{value}</p>
      <p className="text-sm text-gray-500">{label}</p>
    </div>
  )
}

// ─── Events Table ─────────────────────────────────────────────────────────────

function EventsTable({ events }: { events: RecentEvent[] }) {
  if (events.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-gray-400">
        <CalendarDaysIcon className="w-10 h-10 mb-2 opacity-40" />
        <p className="text-sm">Aucun événement</p>
      </div>
    )
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100">
            <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide pb-3 pr-4">Événement</th>
            <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide pb-3 pr-4">Date</th>
            <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wide pb-3 pr-4">Tickets</th>
            <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wide pb-3 pr-4">CA</th>
            <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wide pb-3 pr-4">Entrées</th>
            <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide pb-3 pr-4">Statut</th>
            <th className="pb-3" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {events.map((ev) => (
            <tr key={ev.id} className="hover:bg-gray-50/60 transition-colors">
              <td className="py-3 pr-4">
                <span className="font-medium text-gray-900 line-clamp-1">{ev.name}</span>
              </td>
              <td className="py-3 pr-4 text-gray-500 whitespace-nowrap">{formatDate(ev.event_date)}</td>
              <td className="py-3 pr-4 text-right font-mono text-gray-700">{ev.tickets_sold_total.toLocaleString('fr-FR')}</td>
              <td className="py-3 pr-4 text-right font-mono text-gray-700 whitespace-nowrap">{formatFCFA(ev.revenue_total)}</td>
              <td className="py-3 pr-4 text-right font-mono text-gray-700">{ev.entries_count.toLocaleString('fr-FR')}</td>
              <td className="py-3 pr-4">
                <Badge label={ev.status} variant={ev.status as EventStatus} />
              </td>
              <td className="py-3">
                <Link
                  to={`/events/${ev.id}`}
                  className="text-primary-600 hover:text-primary-700 font-medium text-xs whitespace-nowrap inline-flex items-center gap-1"
                >
                  Détail <ArrowRightIcon className="w-3 h-3" />
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ─── Recent Sales ─────────────────────────────────────────────────────────────

function RecentSaleItem({ sale }: { sale: RecentSale }) {
  const id = sale.buyer_name ?? sale.buyer_phone
  const color = avatarColor(id)
  return (
    <div className="flex items-center gap-3 py-3 border-b border-gray-50 last:border-0">
      <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${color}`}>
        {initials(sale.buyer_name, sale.buyer_phone)}
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-medium text-gray-900 text-sm truncate">
          {sale.buyer_name ?? sale.buyer_phone}
        </p>
        <p className="text-xs text-gray-400 truncate">
          {sale.event_name}
          {sale.ticket_type_name && (
            <span className="ml-1 text-primary-500">· {sale.ticket_type_name}</span>
          )}
        </p>
      </div>
      <div className="flex-shrink-0 text-right">
        <p className="font-mono text-sm font-semibold text-gray-900">{formatFCFA(sale.total_price)}</p>
        <p className="text-xs text-gray-400">{timeAgo(sale.purchased_at)}</p>
      </div>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function DashboardPage() {
  const user = useAuthStore((s) => s.user)
  const [data, setData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null)
  const [refreshing, setRefreshing] = useState(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    else setRefreshing(true)
    setError(null)
    try {
      const result = await dashboardApi.get()
      setData(result)
      setUpdatedAt(new Date())
    } catch {
      if (!silent) setError('Impossible de charger le dashboard. Vérifiez votre connexion.')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [])

  useEffect(() => {
    load()
    intervalRef.current = setInterval(() => load(true), 30_000)
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [load])

  const todayLabel = new Date().toLocaleDateString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  })

  // ── Loading skeleton
  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="animate-pulse space-y-1">
          <div className="w-48 h-8 bg-gray-200 rounded-lg" />
          <div className="w-72 h-4 bg-gray-100 rounded" />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)}
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse">
          <div className="w-40 h-5 bg-gray-100 rounded mb-6" />
          <div className="h-[280px] bg-gray-50 rounded-lg" />
        </div>
      </div>
    )
  }

  // ── Error state
  if (error || !data) {
    return (
      <div className="p-6 flex flex-col items-center justify-center min-h-[400px] text-center">
        <div className="w-12 h-12 bg-danger-50 rounded-full flex items-center justify-center mb-4">
          <ArrowPathIcon className="w-6 h-6 text-danger-600" />
        </div>
        <p className="text-gray-700 font-medium mb-1">Erreur de chargement</p>
        <p className="text-sm text-gray-500 mb-4">{error ?? 'Données non disponibles.'}</p>
        <button
          onClick={() => load()}
          className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
        >
          Réessayer
        </button>
      </div>
    )
  }

  const { kpis, sales_timeline, recent_events, recent_sales } = data
  const firstName = user?.name?.split(' ')[0] ?? 'Admin'

  return (
    <>
      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div className="p-6 space-y-6">

        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Bonjour, {firstName}
            </h1>
            <p className="text-sm text-gray-500 mt-0.5 capitalize">{todayLabel}</p>
            <p className="text-sm text-primary-600 font-medium mt-1">
              {kpis.active_events_count === 0
                ? 'Aucun événement actif'
                : `${kpis.active_events_count} événement${kpis.active_events_count > 1 ? 's' : ''} actif${kpis.active_events_count > 1 ? 's' : ''}`}
            </p>
          </div>
          <button
            onClick={() => load(true)}
            disabled={refreshing}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <ArrowPathIcon className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Actualiser
          </button>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            icon={<TicketIcon className="w-5 h-5" />}
            label="Tickets vendus"
            value={kpis.tickets_sold_total.toLocaleString('fr-FR')}
            trend={<Trend current={kpis.tickets_sold_total} previous={kpis.tickets_sold_yesterday} />}
            accent="text-primary-600 bg-primary-50"
            delay={0}
          />
          <KpiCard
            icon={<CurrencyDollarIcon className="w-5 h-5" />}
            label="Revenus totaux"
            value={formatFCFA(kpis.revenue_total)}
            trend={<Trend current={kpis.revenue_total} previous={kpis.revenue_yesterday} />}
            accent="text-warning-600 bg-warning-50"
            delay={60}
          />
          <KpiCard
            icon={<CalendarDaysIcon className="w-5 h-5" />}
            label="Événements actifs"
            value={kpis.active_events_count.toLocaleString('fr-FR')}
            accent="text-success-600 bg-success-50"
            delay={120}
          />
          <KpiCard
            icon={<CheckBadgeIcon className="w-5 h-5" />}
            label="Entrées aujourd'hui"
            value={kpis.entries_today.toLocaleString('fr-FR')}
            accent="text-purple-600 bg-purple-50"
            delay={180}
          />
        </div>

        {/* Chart */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Ventes — 30 derniers jours</h2>
              <p className="text-xs text-gray-400 mt-0.5">Nombre de tickets vendus par jour</p>
            </div>
          </div>

          {sales_timeline.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-[280px] text-gray-400">
              <ArrowTrendingUpIcon className="w-10 h-10 mb-2 opacity-30" />
              <p className="text-sm">Aucune vente sur la période</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={sales_timeline} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                <defs>
                  <linearGradient id="countGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#6366F1" stopOpacity={0.18} />
                    <stop offset="95%" stopColor="#6366F1" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#F59E0B" stopOpacity={0.12} />
                    <stop offset="95%" stopColor="#F59E0B" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatChartDate}
                  tick={{ fontSize: 11, fill: '#94A3B8', fontFamily: 'DM Mono' }}
                  tickLine={false}
                  axisLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#94A3B8', fontFamily: 'DM Mono' }}
                  tickLine={false}
                  axisLine={false}
                  yAxisId="left"
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={false}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip content={<ChartTooltip />} />
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="count"
                  stroke="#6366F1"
                  strokeWidth={2}
                  fill="url(#countGrad)"
                  dot={false}
                  activeDot={{ r: 4, fill: '#6366F1', stroke: '#fff', strokeWidth: 2 }}
                />
                <Area
                  yAxisId="right"
                  type="monotone"
                  dataKey="revenue"
                  stroke="#F59E0B"
                  strokeWidth={1.5}
                  strokeDasharray="4 2"
                  fill="url(#revenueGrad)"
                  dot={false}
                  activeDot={{ r: 3, fill: '#F59E0B', stroke: '#fff', strokeWidth: 2 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}

          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-50">
            <span className="flex items-center gap-1.5 text-xs text-gray-500">
              <span className="w-5 h-0.5 bg-primary-500 rounded inline-block" /> Tickets vendus
            </span>
            <span className="flex items-center gap-1.5 text-xs text-gray-500">
              <span className="w-5 border-t border-dashed border-warning-500 inline-block" /> Revenus FCFA
            </span>
          </div>
        </div>

        {/* Bottom section */}
        <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">

          {/* Events table — 3/5 */}
          <div className="xl:col-span-3 bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-gray-900">Événements récents</h2>
              <Link
                to="/events"
                className="text-xs text-primary-600 hover:text-primary-700 font-medium inline-flex items-center gap-1"
              >
                Voir tous <ArrowRightIcon className="w-3 h-3" />
              </Link>
            </div>
            <EventsTable events={recent_events} />
          </div>

          {/* Recent sales — 2/5 */}
          <div className="xl:col-span-2 bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-gray-900">Dernières ventes</h2>
              <Link
                to="/tickets"
                className="text-xs text-primary-600 hover:text-primary-700 font-medium inline-flex items-center gap-1"
              >
                Voir tous <ArrowRightIcon className="w-3 h-3" />
              </Link>
            </div>

            {recent_sales.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <TicketIcon className="w-10 h-10 mb-2 opacity-40" />
                <p className="text-sm">Aucune vente récente</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {recent_sales.map((sale) => (
                  <RecentSaleItem key={sale.uuid} sale={sale} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer update indicator */}
        {updatedAt && (
          <div className="flex items-center justify-end gap-2 text-xs text-gray-400 pb-2">
            {refreshing && <ArrowPathIcon className="w-3 h-3 animate-spin" />}
            Mis à jour à{' '}
            {updatedAt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
          </div>
        )}

      </div>
    </>
  )
}
